import React, { useState, useEffect, useContext } from 'react';
import { string, bool, func, array } from 'prop-types';
import classNames from 'classnames/bind';
import { groupWith, sortBy, prop, compose } from 'ramda';

import Autosuggest from 'react-autosuggest';
import parse from 'autosuggest-highlight/parse';

import { gtmPushEvent } from 'utils/gtmPushEvent';
import { withErrorBoundary } from 'containers/ErrorBoundary';

import { StaticTextContext } from 'context/StaticTextContext';
import { SearchContext } from 'context/SearchContext';

import AutosuggestEmpty from 'components/AutosuggestEmpty';
import Hint from 'components/Hint';

import 'shared/AutosuggestPart.scss';
import styles from './AutosuggestPart.module.scss';
const cx = classNames.bind(styles);

/**
 * Escape regex characters.
 * https://developer.mozilla.org/en/docs/Web/JavaScript/Guide/Regular_Expressions#Using_Special_Characters
 * @param {*} str
 */
const escapeRegexCharacters = (str) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Get suggestion.
 * @param {*} value
 */
const getSuggestions = (value, suggestionData) => {
  const escapedValue = escapeRegexCharacters(value.trim());

  if (escapedValue === '' && value.length) {
    return [];
  }
  const regex = new RegExp(escapedValue, 'ig');

  return suggestionData
    .map((item) => {
      return {
        title: item.title,
        groupItems: item.groupItems.filter((groupItem) =>
          regex.test(groupItem.designation)
        ),
      };
    })
    .filter((item) => item.groupItems.length > 0);
};

const renderSectionTitle = (item) => {
  return <strong>{item.title}</strong>;
};

const getSectionSuggestions = (item) => {
  return item.groupItems;
};

/**
 * Get suggestion value.
 * @param {*} suggestion
 */
const getSuggestionValue = (suggestion) => {
  return `${suggestion.designation}`;
};

/**
 * Render suggestions.
 * @param {*} suggestion
 * @param {*} param1
 */
const renderSuggestion = (suggestion, { query }) => {
  const suggestionText = suggestion.designation;
  const startIndex = suggestionText.toLowerCase().indexOf(query.toLowerCase());
  const matches =
    startIndex >= 0 ? [[startIndex, startIndex + query.length]] : [[0, 0]];
  const parts = parse(suggestionText, matches);

  return (
    <span className={'suggestion-content'}>
      <span className="name">
        {parts.map((part, index) => {
          const className = part.highlight ? 'highlight' : null;

          return (
            <span className={className} key={index}>
              {part.text}
            </span>
          );
        })}
      </span>
    </span>
  );
};

const AutosuggestPart = ({
  className,
  type,
  pageType,
  id,
  partValue,
  placeholder,
  disabled,
  updatePartValue,
  partData,
}) => {
  const [isNoSuggestions, setIsNoSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const { labels } = useContext(StaticTextContext);
  const {
    setIsErrorPart,
    setIsPartTooltip,
    setIsEditedPartField,
    isSelectedCar,
    isEditedPartField,
    partName,
    setPartName,
    setPartId,
  } = useContext(SearchContext);

  const inputProps = {
    value: isEditedPartField ? partValue : partName,
    className,
    type,
    id,
    disabled,
    placeholder,
    required: true,
  };

  const onSuggestionsFetchRequested = ({ value }) => {
    const sortedData = sortBy(compose(prop('assemblyGroup')), partData);
    const formatedData = groupWith(
      (a, b) => a.assemblyGroup === b.assemblyGroup,
      sortedData
    ).map((item) => {
      const sortedArray = sortBy(compose(prop('designation')), item);
      return {
        title: item[0].assemblyGroup,
        groupItems: sortedArray,
      };
    });

    const dataSuggestions = getSuggestions(value, formatedData);

    setSuggestions(dataSuggestions);
    setIsNoSuggestions(!dataSuggestions.length);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
    setIsNoSuggestions(false);
  };

  const onChangeHandle = (event, { newValue }) => {
    updatePartValue(newValue);
    setIsEditedPartField(true);

    if (partName) {
      setPartName('');
      setPartId(null);
    }
  };

  const onBlurHandle = (event, { highlightedSuggestion }) => {
    setIsErrorPart(partValue.length > 0);

    if (!isSelectedCar) {
      setIsEditedPartField(false);
    }
  };

  const renderSuggestionsContainer = ({ containerProps, children }) => {
    return (
      <div {...containerProps}>
        {pageType === 'home' && (
          <Hint className={cx('hint')} arrow={true}>
            {labels.tipPartInput}
          </Hint>
        )}
        <div className="react-autosuggest__suggestions-container-wrapper">
          {children}
        </div>
      </div>
    );
  };

  useEffect(() => {
    setIsEditedPartField(false);
  }, []);

  return (
    <div className={cx('root', pageType)}>
      <Autosuggest
        multiSection={true}
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestionsContainer={renderSuggestionsContainer}
        renderSectionTitle={renderSectionTitle}
        getSectionSuggestions={getSectionSuggestions}
        shouldRenderSuggestions={() => true}
        onSuggestionSelected={(
          event,
          { suggestion: { designation, genericArticleId } }
        ) => {
          setPartName(designation);
          setPartId(genericArticleId);
        }}
        renderSuggestion={renderSuggestion}
        inputProps={{
          ...inputProps,
          onFocus: () => {
            setIsPartTooltip(true);
            setIsErrorPart(false);
          },
          onChange: onChangeHandle,
          onBlur: onBlurHandle,
        }}
        id={id}
      />
      {isNoSuggestions && <AutosuggestEmpty searchValue={partValue} />}
    </div>
  );
};

AutosuggestPart.propTypes = {
  className: string,
  type: string.isRequired,
  pageType: string.isRequired,
  id: string.isRequired,
  partValue: string.isRequired,
  disabled: bool,
  partData: array,
  setIsErrorPart: func.isRequired,
  updatePartValue: func.isRequired,
  placeholder: string.isRequired,
};

AutosuggestPart.defaultProps = {
  disabled: false,
  className: null,
  partData: [],
};

export default withErrorBoundary(AutosuggestPart);



// WEBPACK FOOTER //
// ./src/components/AutosuggestPart/AutosuggestPart.js